import { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { login, resendOtp, verifyOtp } from "src/actions/auth";
import Button from "src/components/Shared/Buttons/Button";
import AuthContainer from "src/components/Auth/AuthContainer";
import AuthHeader from "src/components/Auth/AuthHeader";
import AuthDataLayout from "src/layouts/AuthDataLayout";
import TwoFactorForm from "./TwoFactorForm";
import LoginForm from "./LoginForm";

const Login = (props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);

  const [isTwoFactor, setIsTwoFactor] = useState(false);
  const [twoFactorDetails, setTwoFactorDetails] = useState(null);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState(null);

  const [resendOtpSecond, setResendOtpSecond] = useState(0);
  const [disabledResendOtp, setDisabledResendOtp] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  useEffect(() => {
    if (resendOtpSecond <= 0) return;
    const id = setInterval(() => setResendOtpSecond((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendOtpSecond]);

  const loginUser = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (isDisabled) return;

      try {
        setIsDisabled(true);
        const data = await props.login({
          email,
          password,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        setIsDisabled(false);

        if (data?.requireTwoFactor) {
          setIsTwoFactor(true);
          setTwoFactorDetails(data?.twoFactorDetails || null);
          setOtp("");
          setError(null);
          setResendOtpSecond(0);
        } else {
          toast.success(data?.message);
        }
      } catch (err) {
        setIsDisabled(false);
        toast.error(err?.message || "Login failed");
      }
    },
    [email, password, isDisabled, props],
  );

  const otpVerify = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (isSubmitDisabled) return;

      try {
        setIsSubmitDisabled(true);
        setError(null);
        const data = await props.verifyOtp({ email, password, otp });
        toast.success(data?.message);
      } catch (err) {
        const msg = err?.message || "Invalid OTP";
        setError(msg);
        toast.error(msg);
      } finally {
        setIsSubmitDisabled(false);
      }
    },
    [isSubmitDisabled, props, email, otp, password],
  );

  const resendOtp = useCallback(async () => {
    if (disabledResendOtp || resendOtpSecond > 0) return;

    try {
      setDisabledResendOtp(true);
      const data = await props.resendOtp({ email, password });
      toast.info(data?.message);
      setResendOtpSecond(30);
    } catch (err) {
      toast.error(err?.message || "Unable to resend code");
    } finally {
      setDisabledResendOtp(false);
    }
  }, [disabledResendOtp, resendOtpSecond, props, email, password]);

  return (
    <AuthDataLayout>
      <AuthContainer
        header={
          <AuthHeader
            title="Administrator login portal"
            subTitle="Enter your email and password"
          />
        }
        site={props.site}>
        <form
          className="flex w-full flex-col gap-y-2"
          autoComplete="on"
          onSubmit={isTwoFactor ? otpVerify : loginUser}>
          {isTwoFactor ? (
            <TwoFactorForm
              email={email}
              twoFactorDetails={twoFactorDetails}
              disabledResendOtp={disabledResendOtp}
              resendOtpSecond={resendOtpSecond}
              resendOtp={resendOtp}
              otp={otp}
              setOtp={setOtp}
              error={error}
              otpVerify={otpVerify}
              isSubmitDisabled={isSubmitDisabled}
            />
          ) : (
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              loginUser={loginUser}
            />
          )}
        </form>

        {!isTwoFactor && (
          <>
            <div className="flex w-full justify-end">
              <Link
                to="/forgot-password/submit-email"
                className="my-2 text-sm text-slate-800">
                Forgot Password?
              </Link>
            </div>

            <div className="mt-2">
              <Button
                className="h-9 !py-0 !font-normal"
                width="w-full"
                type="submit"
                disabled={isDisabled}
                onClick={loginUser}>
                Login
              </Button>
            </div>
          </>
        )}
      </AuthContainer>
    </AuthDataLayout>
  );
};

const mapStateToProps = (state) => ({ site: state.site });

export default connect(mapStateToProps, { login, verifyOtp, resendOtp })(Login);
